import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import SEO from '../components/SEO'
import LightboxModal from '../components/LightboxModal'
import ProductCard from '../components/ProductCard'
import { fetchBySlug, fetchCollectionV2 } from '../lib/cms'

export default function ProductDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const [product, setProduct] = useState(null)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  useEffect(() => {
    async function loadProduct() {
      setLoading(true)
      try {
        let item = await fetchBySlug(slug)
        if (!item) {
          // Try search by field or list
          const list = await fetchCollectionV2('produkter', { limit: 60 })
          item = (list.items || []).find((p) => (p.slug?._ || p.slug) === slug)
        }

        if (item) {
          setProduct(item)
          setActiveImageIndex(0)

          // Load related products in same category
          if (item.produktkategori) {
            const relData = await fetchCollectionV2('produkter', {
              produktkategori: item.produktkategori,
              limit: 4,
            })
            setRelatedProducts(
              (relData.items || []).filter((p) => (p.slug?._ || p.slug) !== slug).slice(0, 3)
            )
          }
        } else {
          setProduct(null)
        }
      } catch (err) {
        console.error('Failed to load product:', err)
      } finally {
        setLoading(false)
      }
    }
    loadProduct()
  }, [slug])

  if (loading) {
    return (
      <div className="py-28 text-center text-slate-400">
        <i className="fa-solid fa-spinner fa-spin text-3xl mb-3 text-sky-500" />
        <p className="text-sm">Henter produktinformation...</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="py-24 max-w-xl mx-auto text-center px-4 space-y-4">
        <div className="text-amber-500 text-4xl mx-auto">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Produktet blev ikke fundet</h1>
        <p className="text-sm text-slate-600">
          Det efterspurgte produkt findes desværre ikke i vores katalog, eller er midlertidigt udgået.
        </p>
        <div className="pt-2">
          <Link
            to="/produkter"
            className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors"
          >
            <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
            <span>Tilbage til produkter</span>
          </Link>
        </div>
      </div>
    )
  }

  // Aggregate all unique images for this product
  const primaryImg = Array.isArray(product.produktbillede)
    ? product.produktbillede[0]
    : product.produktbillede
  const moreImgs = Array.isArray(product.flere_produktbilleder)
    ? product.flere_produktbilleder
    : product.flere_produktbilleder
    ? [product.flere_produktbilleder]
    : []

  const allImages = Array.from(new Set([primaryImg, ...moreImgs].filter(Boolean)))
  const currentMainImage = allImages[activeImageIndex] || primaryImg

  const categoryLabels = {
    kabiner: 'Kabiner',
    vandpoleringsmaskiner: 'Vandpoleringsmaskiner',
    'mobile-blaesere': 'Mobile Blæsere',
    kompressorer: 'Kompressorer',
    toerisblaesning: 'Tørisblæsning',
    forbrugsmaterialer: 'Forbrugsmaterialer & Blæsemedier',
    'reservedele-tilbehoer': 'Reservedele & Tilbehør',
    'kompressorer-udstyr': 'Mobile Blæsere & Kompressorer',
  }
  let targetCatSlug = product.produktkategori
  if (targetCatSlug === 'kabiner') {
    const n = (product.produktnavn || '').toLowerCase()
    if (n.includes('wet') || n.includes('vandpoler') || n.includes('kwb')) {
      targetCatSlug = 'vandpoleringsmaskiner'
    } else {
      targetCatSlug = 'kabiner'
    }
  } else if (targetCatSlug === 'kompressorer-udstyr') {
    const n = (product.produktnavn || '').toLowerCase()
    if (n.includes('ibix') || n.includes('blaster') || n.includes('mobil')) {
      targetCatSlug = 'mobile-blaesere'
    } else {
      targetCatSlug = 'kompressorer'
    }
  }
  const categoryName = categoryLabels[targetCatSlug] || categoryLabels[product.produktkategori] || product.produktkategori

  return (
    <>
      <SEO
        title={product.meta_title || `${product.produktnavn} – Kryger Wet Blaster`}
        description={product.meta_description}
        ogImage={primaryImg}
      />

      {/* Breadcrumb Bar */}
      <div className="bg-white border-b border-slate-200/80 py-3 text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-sky-600 transition-colors">
            Forside
          </Link>
          <span className="text-slate-300">/</span>
          <Link to="/produkter" className="hover:text-sky-600 transition-colors">
            Produkter
          </Link>
          {product.produktkategori && (
            <>
              <span className="text-slate-300">/</span>
              <Link
                to={`/produkter?kategori=${targetCatSlug || product.produktkategori}`}
                className="hover:text-sky-600 transition-colors"
              >
                {categoryName}
              </Link>
            </>
          )}
          <span className="text-slate-300">/</span>
          <span className="text-slate-900 font-medium truncate max-w-xs sm:max-w-md">
            {product.produktnavn}
          </span>
        </div>
      </div>

      {/* Product Hero & Info Section */}
      <section className="py-10 lg:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: Gallery & Main Image (7 cols) */}
            <div className="lg:col-span-7 space-y-4">
              {/* Main Image View */}
              <div className="relative aspect-[4/3] w-full bg-white rounded-none border border-slate-200/90 shadow-sm overflow-hidden flex items-center justify-center p-6 group">
                {currentMainImage ? (
                  <img
                    src={currentMainImage}
                    alt={product.produktnavn}
                    className="max-h-full max-w-full object-contain cursor-zoom-in transition-transform duration-300 group-hover:scale-[1.02]"
                    onClick={() => setLightboxIndex(activeImageIndex)}
                  />
                ) : (
                  <div className="text-slate-300 text-center">
                    <i className="fa-solid fa-industry text-6xl mb-2" aria-hidden="true" />
                    <p className="text-xs text-slate-400">Billede undervejs</p>
                  </div>
                )}

                {/* Click to enlarge indicator */}
                {currentMainImage && (
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(activeImageIndex)}
                    className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900/80 hover:bg-slate-900 text-white text-xs font-medium backdrop-blur-xs transition-colors shadow"
                    aria-label="Åbn billede i fuld størrelse"
                  >
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                    <span>Zoom</span>
                  </button>
                )}
              </div>

              {/* Thumbnail Gallery (if more than 1 image) */}
              {allImages.length > 1 && (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                  {allImages.map((img, idx) => {
                    const isSelected = idx === activeImageIndex
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveImageIndex(idx)}
                        className={`aspect-square rounded-none bg-white p-2 border overflow-hidden transition-all cursor-pointer ${
                          isSelected
                            ? 'border-sky-500 ring-2 ring-sky-500/20 shadow-sm'
                            : 'border-slate-200 hover:border-slate-300 opacity-80 hover:opacity-100'
                        }`}
                        aria-label={`Vis produktbillede ${idx + 1}`}
                      >
                        <img
                          src={img}
                          alt={`${product.produktnavn} miniature ${idx + 1}`}
                          className="w-full h-full object-contain"
                        />
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Technical guarantee highlight */}
              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-2xs flex items-center justify-between gap-4 text-xs text-slate-600">
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-emerald-500 text-sm" aria-hidden="true" />
                  <span>
                    {product.produktnavn?.includes('PART-BLASTER')
                      ? 'Tysk kvalitet & CE-godkendt'
                      : 'Dansk kvalitet & CE-godkendt'}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-truck-fast text-sky-500 text-sm" aria-hidden="true" />
                  <span>Hurtig levering eller afhentning</span>
                </span>
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-file-invoice text-slate-500 text-sm" aria-hidden="true" />
                  <span>Fakturaafregning</span>
                </span>
              </div>
            </div>

            {/* Right: Product Details & Order CTA (5 cols) */}
            <div className="lg:col-span-5 space-y-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200/90 shadow-sm">
              {/* Category Badge */}
              {categoryName && (
                <div className="inline-block bg-sky-100 text-sky-800 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                  {categoryName}
                </div>
              )}

              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-snug">
                {product.produktnavn}
              </h1>

              {/* Primary Call to Action Button */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center justify-between text-xs text-slate-600">
                  <span className="font-semibold text-slate-800">Bestillingsforespørgsel</span>
                  <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Klar til ordre / faktura
                  </span>
                </div>

                <Link
                  to={`/bestil?produkt=${slug}`}
                  className="w-full flex items-center justify-center gap-2.5 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-bold py-3.5 px-6 rounded-lg shadow-md transition-all text-sm group"
                >
                  <i className="fa-solid fa-file-invoice text-base" aria-hidden="true" />
                  <span>Bestil produkt</span>
                  <i className="fa-solid fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>

                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  Bestilling sker uforpligtende med automatisk modtagelse af bekræftelse og efterfølgende fakturering.
                </p>
              </div>

              {/* Direct phone / contact shortcut */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                <span>Spørgsmål om denne vare?</span>
                <a
                  href="tel:+4521438149"
                  className="text-sky-600 hover:text-sky-700 font-semibold whitespace-nowrap inline-flex items-center gap-1"
                >
                  <i className="fa-solid fa-phone text-[10px]" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring 21 43 81 49</span>
                </a>
              </div>

              {/* Product Description */}
              <div className="pt-4 border-t border-slate-100">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-3">
                  Beskrivelse
                </h2>
                <div
                  className="prose prose-sm prose-slate max-w-none text-slate-700 leading-relaxed space-y-3"
                  dangerouslySetInnerHTML={{ __html: product.produktbeskrivelse }}
                />
              </div>

              {/* Specifications if present */}
              {product.specifikationer && (
                <div className="pt-4 border-t border-slate-100">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-3">
                    Specifikationer
                  </h2>
                  <div
                    className="prose prose-sm prose-slate max-w-none text-slate-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: product.specifikationer }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for full screen image preview */}
      {lightboxIndex !== null && (
        <LightboxModal
          images={allImages}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <section className="py-14 bg-white border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                Andre produkter inden for {categoryName}
              </h2>
              <Link
                to={`/produkter?kategori=${product.produktkategori}`}
                className="text-xs font-semibold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1"
              >
                <span>Se alle</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedProducts.map((rel) => (
                <ProductCard key={rel.id || (rel.slug?._ || rel.slug)} product={rel} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  )
}
